import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import { CheckCircleIcon, EyeIcon } from '@heroicons/react/outline';

const CAUSA_LABELS = {
  todos: 'Todos los datos',
  marca_modelo: 'Marca y/o modelo',
  otros: 'Otros (año, tipo, etc.)'
};

/**
 * Panel de administración: reportes de clientes cuando los datos de consulta por patente
 * no coinciden con su vehículo. Incluye sugerencia de ingresar información manualmente.
 */
const AdminReportesDatosVehiculo = () => {
  const { user } = useAuth();
  const [reportes, setReportes] = useState([]);
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('nuevo');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [stats, setStats] = useState({ total: 0, nuevo: 0, visto: 0, resuelto: 0 });

  useEffect(() => {
    cargarReportes();
    cargarClientes();
  }, [filtroEstado]);

  const cargarClientes = async () => {
    try {
      const snap = await getDocs(collection(db, 'clientes'));
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      setClientes(map);
    } catch (e) {
      console.warn('Error cargando clientes:', e);
    }
  };

  const cargarReportes = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'reportes_datos_vehiculo'));
      let data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (filtroEstado !== 'todos') {
        data = data.filter(r => (r.estado || 'nuevo') === filtroEstado);
      }
      data.sort((a, b) => {
        const ta = a.fechaReporte?.toDate?.()?.getTime() ?? 0;
        const tb = b.fechaReporte?.toDate?.()?.getTime() ?? 0;
        return tb - ta;
      });
      setReportes(data);

      const allSnap = await getDocs(collection(db, 'reportes_datos_vehiculo'));
      const counts = { total: allSnap.size, nuevo: 0, visto: 0, resuelto: 0 };
      allSnap.docs.forEach(d => {
        const est = d.data().estado || 'nuevo';
        if (counts.hasOwnProperty(est)) counts[est]++;
      });
      setStats(counts);
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoading(false);
    }
  };

  const actualizarEstado = async (reporteId, nuevoEstado) => {
    try {
      const ref = doc(db, 'reportes_datos_vehiculo', reporteId);
      await updateDoc(ref, {
        estado: nuevoEstado,
        fechaActualizacion: serverTimestamp(),
        actualizadoPor: user?.uid
      });
      await cargarReportes();
    } catch (e) {
      console.error('Error actualizando reporte:', e);
    }
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      nuevo: 'bg-amber-100 text-amber-800 border-amber-300',
      visto: 'bg-blue-100 text-blue-800 border-blue-300',
      resuelto: 'bg-green-100 text-green-800 border-green-300'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[estado] || 'bg-gray-100 text-gray-800'}`}>
        {estado}
      </span>
    );
  };

  let reportesFiltrados = reportes;
  if (filtroCliente) {
    reportesFiltrados = reportes.filter(r => (r.clienteId || '') === filtroCliente);
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Reportes: datos de vehículo no coinciden</h2>
        <p className="text-sm text-gray-600 mb-6">
          Los clientes reportan cuando la consulta por patente devuelve datos que no coinciden con su vehículo. Se les sugiere ingresar la información manualmente.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-700">Nuevos</p>
            <p className="text-2xl font-bold text-amber-700">{stats.nuevo}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">Vistos</p>
            <p className="text-2xl font-bold text-blue-700">{stats.visto}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-700">Resueltos</p>
            <p className="text-2xl font-bold text-green-700">{stats.resuelto}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center mb-4">
          {['nuevo', 'visto', 'resuelto', 'todos'].map(est => (
            <button
              key={est}
              onClick={() => setFiltroEstado(est)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                filtroEstado === est ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {est === 'todos' ? 'Todos' : est.charAt(0).toUpperCase() + est.slice(1)}
            </button>
          ))}
          <label className="ml-4 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Cliente:</span>
            <select
              value={filtroCliente}
              onChange={e => setFiltroCliente(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="">Todos</option>
              {[...new Set(reportes.map(r => r.clienteId))].filter(Boolean).map(cid => (
                <option key={cid} value={cid}>
                  {clientes[cid]?.nombre || cid}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : reportesFiltrados.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-600">
          No hay reportes {filtroEstado !== 'todos' ? `con estado "${filtroEstado}"` : ''}
          {filtroCliente ? ' para el cliente seleccionado' : ''}.
        </div>
      ) : (
        <div className="space-y-4">
          {reportesFiltrados.map(reporte => {
            const cliente = clientes[reporte.clienteId];
            return (
              <div
                key={reporte.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">Patente: {reporte.patente}</span>
                      {getEstadoBadge(reporte.estado || 'nuevo')}
                      <span className="text-sm text-gray-500">
                        {reporte.fechaReporte?.toDate
                          ? reporte.fechaReporte.toDate().toLocaleString('es-CL')
                          : '—'}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      <span className="font-medium">Cliente:</span>{' '}
                      {cliente?.nombre || reporte.clienteEmail || reporte.clienteId || '—'}
                      {reporte.clienteEmail && (
                        <span className="ml-2 text-gray-500">({reporte.clienteEmail})</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Causa del reporte</p>
                  <p className="text-sm text-gray-900">{CAUSA_LABELS[reporte.causa] || reporte.causa}</p>
                  {reporte.detalle && (
                    <p className="text-sm text-gray-600 mt-1 italic">&quot;{reporte.detalle}&quot;</p>
                  )}
                </div>

                {reporte.datosMostrados && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Datos mostrados por la API (que no coinciden)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      {Object.entries(reporte.datosMostrados).map(([k, v]) => (
                        <div key={k}><span className="text-gray-500">{k}:</span> {String(v ?? '—')}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="text-sm text-amber-900">
                    <strong>Sugerencia al cliente:</strong> Ingresar la información manualmente en la pestaña &quot;Ingreso manual&quot; al agregar vehículo.
                  </p>
                </div>

                {(reporte.estado === 'nuevo' || reporte.estado === 'visto') && (
                  <div className="flex gap-2 flex-wrap">
                    {reporte.estado === 'nuevo' && (
                      <button
                        onClick={() => actualizarEstado(reporte.id, 'visto')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        <EyeIcon className="h-4 w-4" /> Marcar visto
                      </button>
                    )}
                    <button
                      onClick={() => actualizarEstado(reporte.id, 'resuelto')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4" /> Marcar resuelto
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReportesDatosVehiculo;
